import { useEffect, useRef, type ReactNode } from 'react'
import { useBackdropClose } from '../ui'

/**
 * Barrierearme Dialog-Grundlage (H7). Räumt die duplizierten `fixed inset-0`-Divs
 * ab und liefert einheitlich: role="dialog" + aria-modal, Fokus-Trap (Tab bleibt
 * im Dialog), Escape schließt, Backdrop-Klick schließt, Autofokus auf das erste
 * fokussierbare Element, Body-Scroll-Lock. Tastatur- und Screenreader-Nutzer
 * sitzen nicht mehr fest.
 */
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'

export function Modal({
  open = true,
  onClose,
  labelledBy,
  label,
  children,
  className = '',
  align = 'center',
}: {
  open?: boolean
  onClose: () => void
  /** id eines Titel-Elements im Dialog (aria-labelledby). */
  labelledBy?: string
  /** Fallback-Beschriftung, falls kein sichtbarer Titel referenziert wird. */
  label?: string
  children: ReactNode
  className?: string
  align?: 'center' | 'end'
}): React.JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null)
  const prevFocus = useRef<HTMLElement | null>(null)
  const backdrop = useBackdropClose(onClose)

  useEffect(() => {
    if (!open) return
    prevFocus.current = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    // Autofokus: erstes fokussierbares Element, sonst der Panel-Container.
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? panel)?.focus()

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key === 'Tab' && panel) {
        const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null)
        if (items.length === 0) {
          e.preventDefault()
          panel.focus()
          return
        }
        const firstEl = items[0]!
        const lastEl = items[items.length - 1]!
        const active = document.activeElement as HTMLElement | null
        if (e.shiftKey && (active === firstEl || active === panel)) {
          e.preventDefault()
          lastEl.focus()
        } else if (!e.shiftKey && active === lastEl) {
          e.preventDefault()
          firstEl.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey, true)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = prevOverflow
      prevFocus.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center bg-black/40 p-4 ${align === 'end' ? 'items-end sm:items-center' : 'items-center'}`}
      {...backdrop}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
        className={`max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl p-5 shadow-xl outline-none ${className}`}
        style={{ backgroundColor: 'var(--color-card)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}
